export type Papel = "administrativo" | "medico";

export type Sexo = "M" | "F";

export interface Empresa {
  id: number;
  razao_social: string;
  cnpj: string;
  endereco: string | null;
  cidade: string | null;
  uf: string | null;
  cep: string | null;
  cnae: string | null;
  grau_risco: number | null;
  ativa: boolean;
}

export interface Unidade {
  id: number;
  empresa_id: number;
  nome: string;
  cnpj_local: string | null;
  contratante: string | null;
  endereco: string | null;
  cidade: string | null;
  uf: string | null;
  ativa: boolean;
}

export interface Setor {
  id: number;
  unidade_id: number;
  nome: string;
}

export interface Funcao {
  id: number;
  setor_id: number;
  nome: string;
  cbo: string | null;
}

export interface Medico {
  id: number;
  nome: string;
  crm: string;
  uf_crm: string;
  rqe: string | null;
  especialidade: string | null;
  entra_object_id: string | null;
  ativo: boolean;
}

export interface Colaborador {
  id: number;
  empresa_id: number;
  unidade_id: number;
  setor_id: number;
  funcao_id: number;
  nome: string;
  cpf: string;
  rg: string | null;
  data_nascimento: string | null;
  sexo: Sexo | null;
  ativo: boolean;
}

// ---------- Bloco operacional: pendências → atendimento → parecer → ASO ----------

export type TipoExameOcupacional =
  | "admissional"
  | "periodico"
  | "retorno_trabalho"
  | "mudanca_risco"
  | "demissional"
  | "monitoracao_pontual";

export type StatusAtendimento = "em_andamento" | "concluido" | "cancelado";
export type ResultadoParecer = "apto" | "apto_com_restricao" | "inapto";
/**
 * Ciclo de vida do ASO. O papel impresso e assinado à mão é o registro oficial
 * (a clínica não tem certificação SBIS/CFM), então o status acompanha o
 * documento físico: gerado → impresso → assinado. Só `cancelado` não trava o
 * atendimento.
 */
export type StatusAso = "gerado" | "impresso" | "assinado" | "cancelado";
export type SituacaoExame = "pendente" | "vencido" | "valido";

export const STATUS_ASO_BADGE: Record<StatusAso, { rotulo: string; classe: string }> = {
  gerado: { rotulo: "gerado", classe: "badge bg-gray-100 text-ink-muted" },
  impresso: { rotulo: "impresso", classe: "badge bg-amber-100 text-amber-800" },
  assinado: { rotulo: "assinado", classe: "badge bg-emerald-100 text-emerald-800" },
  cancelado: { rotulo: "cancelado", classe: "badge bg-red-100 text-red-800 line-through" },
};

/** Digitalizações anexadas ao atendimento (o papel assinado, em cópia). */
export type TipoAnexo = "ficha_assinada" | "aso_assinado" | "resultado_exame" | "outro";

export const TIPO_ANEXO_ROTULOS: Record<TipoAnexo, string> = {
  ficha_assinada: "Ficha clínica assinada",
  aso_assinado: "ASO assinado",
  resultado_exame: "Resultado de exame",
  outro: "Outro documento",
};

export interface AnexoAtendimento {
  id: number;
  atendimento_id: number;
  tipo: TipoAnexo;
  descricao: string | null;
  arquivo_blob: string;
  nome_arquivo: string;
  content_type: string | null;
  tamanho_bytes: number;
  enviado_por: string;
  enviado_em: string;
}

export const TIPO_EXAME_ROTULOS: Record<TipoExameOcupacional, string> = {
  admissional: "Admissional",
  periodico: "Periódico",
  retorno_trabalho: "Retorno ao Trabalho",
  mudanca_risco: "Mudança de Risco",
  demissional: "Demissional",
  monitoracao_pontual: "Monitoração Pontual",
};

export const RESULTADO_PARECER_ROTULOS: Record<ResultadoParecer, string> = {
  apto: "Apto",
  apto_com_restricao: "Apto com restrição",
  inapto: "Inapto",
};

export interface Pendencia {
  colaborador_id: number;
  colaborador: string;
  empresa: string;
  funcao: string;
  exame: string;
  situacao: SituacaoExame;
  periodicidade_meses: number | null;
  ultima_realizacao: string | null;
  vence_em: string | null;
}

export interface AtendimentoResumo {
  id: number;
  tipo: TipoExameOcupacional;
  data_atendimento: string;
  status: StatusAtendimento;
  colaborador_id: number;
  colaborador: string;
  empresa_id: number;
  empresa: string;
  medico: string;
}

export interface AtendimentoCriado {
  id: number;
  colaborador_id: number;
  medico_id: number;
  tipo: TipoExameOcupacional;
  data_atendimento: string;
  documento_pcmso_id: number | null;
  ghe_id: number | null;
  status: StatusAtendimento;
  criado_em: string;
  alerta_dispensa?: string;
}

export interface RiscoGheDetalhe {
  grupo: string;
  perigo: string;
  agravos: string | null;
}

export interface AptidaoGheDetalhe {
  id: number;
  nome: string;
  frase_aso: string;
}

export interface ExigenciaGhe {
  exame_id: number;
  exame: string;
  periodicidade_meses: number | null;
  admissional: boolean;
  retorno_trabalho: boolean;
  mudanca_risco: boolean;
  demissional: boolean;
  observacao: string | null;
  situacao: SituacaoExame | null;
  ultima_realizacao: string | null;
  vence_em: string | null;
}

export interface FichaClinica {
  anamnese: string | null;
  exame_fisico: string | null;
  antecedentes: string | null;
  preenchido_por?: number;
  preenchido_em?: string;
}

export interface ExameRealizado {
  id: number;
  exame_id: number;
  exame: string;
  data_realizacao: string;
  resultado: string | null;
  observacao: string | null;
  /** true = pedido pela empresa cliente, fora da bateria do PCMSO */
  solicitado_pela_empresa: boolean;
}

/** Item do catálogo de exames (busca livre pros pedidos da empresa). */
export interface Exame {
  id: number;
  nome: string;
}

export interface ParecerAptidao {
  aptidao_id: number;
  nome: string;
  frase_aso: string;
  apto: boolean;
}

export interface Parecer {
  resultado: ResultadoParecer;
  restricoes: string | null;
  emitido_por: string;
  emitido_em: string;
  aptidoes: ParecerAptidao[];
}

export interface AsoDoAtendimento {
  id: number;
  numero: string;
  status: StatusAso;
  gerado_em: string;
  /**
   * Só vêm nas respostas das mutações de ASO (marcar impresso/assinado,
   * cancelar). O detalhe do atendimento hoje devolve apenas os 4 campos acima.
   */
  cancelado_motivo?: string | null;
  cancelado_por?: string | null;
  cancelado_em?: string | null;
}

export interface AtendimentoDetalhe {
  id: number;
  tipo: TipoExameOcupacional;
  data_atendimento: string;
  status: StatusAtendimento;
  documento_pcmso_id: number | null;
  /** Escopo (contrato/lotação) do PCMSO congelado no atendimento; "" = empresa toda. */
  pcmso_escopo: string | null;
  ghe_id: number | null;
  criado_em: string;
  colaborador: {
    id: number;
    nome: string;
    cpf: string;
    data_nascimento: string | null;
    sexo: Sexo | null;
    funcao: string;
    setor: string;
    unidade: string;
    empresa_id: number;
    empresa: string;
  };
  medico: { id: number; nome: string; crm: string; uf_crm: string };
  ghe:
    | { id: number; codigo: string; nome: string | null; riscos: RiscoGheDetalhe[]; aptidoes: AptidaoGheDetalhe[] }
    | null;
  exigencias: ExigenciaGhe[];
  ficha: FichaClinica | null;
  cancelado_motivo: string | null;
  cancelado_por: string | null;
  cancelado_em: string | null;
  exames_realizados: ExameRealizado[];
  parecer: Parecer | null;
  aso: AsoDoAtendimento | null;
}

export interface Aso {
  id: number;
  numero: string;
  status: StatusAso;
  gerado_em: string;
  atendimento_id: number;
  colaborador: string;
  empresa: string;
  tipo: TipoExameOcupacional;
  resultado: ResultadoParecer | null;
}
